/* eslint-disable max-lines */
import Select from '@taglivros/oef_componentes/build/components/select'
import StyleGuide from '@taglivros/oef_componentes/build/components/styleGuide'
import Input from '@taglivros/oef_componentes/build/components/input'
import Button from '@taglivros/oef_componentes/build/components/button'

import React, { useEffect } from 'react'
import { func, object, string } from 'prop-types'
import CEPInput from '../CEPInput'
import NumberInput from '../NumberInput'
import entregaIcon from '../../assets/imagens/icon-entrega.svg'
import styled from 'styled-components'
import { INVALID_CEP, VALID_STATES, REQUIRED_MESSAGE } from '../../constants'
import { getEnderecoPorCEP } from '../../api/endereco.servico'
import { isEmptyObject, empty } from '../../utils/helpers/helpers'
import InlineLoader from '../InlineLoader/InlineLoader'
import { useEndereco } from '../../contexts/EnderecoContext'

const states = VALID_STATES.map( estado => ( {
  'codigo': estado,
  'value': estado,
  'visualizacao': estado
} ) )

const URL_CORREIOS = `http://www.buscacep.correios.com.br/sistemas/buscacep/`

const LoadingWrapper = styled.div`
  align-items: center;
  background: rgba(255,255,255,0.7);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 100%;
  z-index: 2;

  > div {
    margin: 0;
  }
`

const CardEntrega = styled.div`
  border: 1px solid ${StyleGuide.cores.cinza200};
  display: none;
  flex-wrap: wrap;
  margin: -20px auto 10px;
  padding: 16px 8px;
  width: 100%;

  @media(max-width: ${StyleGuide.mediaQuery.mobile}) {
    margin: 0px 0 40px;
  }
`

const Titulo = styled.p`
  ${StyleGuide.tipografia.textoPequeno};
  color: ${StyleGuide.cores.cinza500};
  display: inline-flex;
  margin: 0;

  .destaque {
    ${StyleGuide.boldFonte};
  }
`

const BotaoWrapper = styled.div`
  display: flex;
  justify-content: flex-end;

  @media (max-width: ${StyleGuide.mediaQuery.mobile}) {
    flex-direction: column;
  }

  button {
    margin-right: 10px;

    &:last-of-type {
      margin-right: 0px;
    }

    @media (max-width: ${StyleGuide.mediaQuery.mobile}) {
      margin-right: 0px;
      margin-top: 10px;

      &:first-of-type {
        margin-top: 0px;
      }
    }
  }
`

const Icon = styled.span`
  background-image: url(${entregaIcon});
  display: inline-flex;
  height: 15px;
  margin-right: 5px;
  width: 23px;
`

const Descricao = styled.p`
  ${StyleGuide.tipografia.textoHelper};
  margin: 5px 0 0;
  width: 100%;
`

const WrapperEnderecoForm = styled.div`
  width: 100%;

  .card.align {
    text-align: right;
  }

  .link--cep {
    ${StyleGuide.tipografia.textoHelper}
    color: ${StyleGuide.cores.azul};
    margin-left: 30px;
    text-decoration: underline;

    @media (max-width: ${StyleGuide.mediaQuery.mobile}) {
      margin-bottom: 12px;
      max-width: 150px;
      width: 100%;
    }
  }

  .row--form {
    & > div {
      z-index: 1;
    }

    @media (min-width: ${StyleGuide.mediaQuery.mobile}) {
      margin-bottom: 20px;
    }

    .uf--field {
      @media (min-width: ${StyleGuide.mediaQuery.mobile}) {
        width: calc(100%/3.5);
      }
    }
  }

  .row--double {
    & > div {
      z-index: 1;
    }

    @media (min-width: ${StyleGuide.mediaQuery.mobile})  {
      align-items: center;
      display: flex;
      justify-content: space-between;
    }
  }

  .row--uf {
    align-items: flex-start;
  }

  .row--cep {
    justify-content: flex-start;

    @media (max-width: ${StyleGuide.mediaQuery.mobile}) {
      align-items: center;
      display: flex;
    }
  }
`

const EnderecoForm = ( { endereco, onCancelar, onConfirmar, enviarEndereco, tema } ) => {
  const [ state, setState ] = React.useState( endereco )
  const [ erros, setErros ] = React.useState( {} )
  const [ sucesso, setSucesso ] = React.useState( {} )
  const [ camposObrigatoriosPreenchidos, setCamposObrigatoriosPreenchidos ] = React.useState( false )
  const { loadingRequisicaoNovoEndereco } = useEndereco()
  const camposObrigatorios = [ 'cep', 'rua', 'numero', 'bairro', 'uf', 'cidade' ]

  const validate = ( event, isOnSubmit=false ) => {
    const { name, value } = !isEmptyObject( event ) && event.target
    if ( name === 'cep' && state.cep.length < 8 ) {
      setErros( {
        ...erros,
        [ name ]: INVALID_CEP
      } )
    } else if ( value === '' && !isOnSubmit ) {
      setErros( {
        ...erros,
        [ name ]: REQUIRED_MESSAGE
      } )
    } else if ( !camposObrigatoriosPreenchidos && isOnSubmit ) {
      const obrigatoriosNaoPreenchidos = camposObrigatorios.reduce( ( acumulador, obrigatorio ) => {
        if ( empty( state[ obrigatorio ] ) ) {
          return {
            ...acumulador,
            [ obrigatorio ]: REQUIRED_MESSAGE
          }
        }

        return null
      }, {} )
      setErros( obrigatoriosNaoPreenchidos )
    }
  }

  useEffect( () => {
    setState( endereco )

    if ( !isEmptyObject( endereco ) ) {
      setErros( {} )
      setSucesso( {
        'bairro': true,
        'cep': true,
        'cidade': true,
        'complemento': Boolean( endereco.complemento ),
        'numero': true,
        'rua': true,
        'uf': endereco.uf !== 'UF'
      } )
    }
  }, [ endereco ] )

  useEffect( () => {
    if ( enviarEndereco && isEmptyObject( erros ) ) {
      enviarEndereco( state )
    }

    if ( !isEmptyObject( state ) ) {
      const hasPreenchimento = camposObrigatorios.reduce( ( acumulador, campoObrigatorio ) => {
        return !empty( state[ campoObrigatorio ] ) && acumulador && state[ campoObrigatorio ] !== 'UF'
      }, true )

      setCamposObrigatoriosPreenchidos( hasPreenchimento )
    }
  }, [ erros, state ] )

  useEffect( () => {
    if ( camposObrigatoriosPreenchidos && !onCancelar ) {
      onConfirmar( state )
    }
  }, [ camposObrigatoriosPreenchidos, state ] )

  const handleChange = event => {
    if ( event.codigo ) {
      if ( event.codigo === 'UF' ) {
        setErros( {
          ...erros,
          'uf': REQUIRED_MESSAGE
        } )
        setSucesso( {
          ...sucesso,
          'uf': false
        } )
      } else {
        setErros( {
          ...erros,
          'uf': null
        } )

        setSucesso( {
          ...sucesso,
          'uf': true
        } )
      }

      setState( {
        ...state,
        'uf': event.codigo
      } )
    }

    if ( event.target ) {
      const { name, value } = event.target

      if ( name === 'complemento' ) {
        if ( value.length<=50 ) {
          setState( {
            ...state,
            [ name ]: value
          } )
        }
      } else {
        setState( {
          ...state,
          [ name ]: value
        } )
      }
      setErros( {
        ...erros,
        [ name ]: null
      } )
    }
  }

  const handleBlur = event => {
    const { name } = event.target
    if ( camposObrigatorios.includes( name ) ) {
      validate( event )
    }
    setSucesso( {
      ...sucesso,
      [ name ]: empty( erros[ name ] ) && !empty( state[ name ] )
    } )
  }

  const handleCEPBlur = ( { 'target': { name } } ) => {
    getEnderecoPorCEP( state.cep )
      .then( ( enderecoGerado ) => {
        setState( ( prevState ) => ( {
          ...prevState,
          ...enderecoGerado
        } ) )

        setErros( ( prevState ) => ( {
          ...prevState,
          'bairro': null,
          'cep': null,
          'cidade': null,
          'rua': null,
          'uf': null
        } ) )

        setSucesso( {
          ...sucesso,
          'bairro': true,
          'cep': true,
          'cidade': true,
          'rua': true,
          'uf': true
        } )
      } )
      .catch( ( ) => {
        setSucesso( {} )
        setErros( { [ name ]: INVALID_CEP } )
        setState( {} )
      } )
  }

  const handleSubmit = event => {
    onConfirmar( state )
    event.preventDefault()
    validate( event, true )
    if ( onConfirmar ) {
      onConfirmar( state )
    }
  }
  return (
    <WrapperEnderecoForm>
      {loadingRequisicaoNovoEndereco ?
        <LoadingWrapper>
          <InlineLoader
            cor={ tema }
            isCenter
          />
        </LoadingWrapper> : null}

      <form onSubmit={ handleSubmit }>
        <div className="row--form row--double row--cep">
          <CEPInput
            column={ 2.5 }
            erro={ erros.cep }
            label="CEP"
            name="cep"
            onBlur={ handleCEPBlur }
            onChange={ handleChange }
            onPressEnter={ handleCEPBlur }
            sucesso={ sucesso.cep }
            tipo={ Input.TIPO_TEL }
            value={ state.cep }
          />

          <a
            className="link--cep"
            href={ URL_CORREIOS }
            rel="noopener noreferrer"
            target="_blank"
          >
            Não sei meu CEP
          </a>
        </div>

        <div className="row--double row--cep">
          <CardEntrega>
            <Icon />

            <Titulo>
              <span className="destaque">{'Previsão de entrega: '}</span>
              12 a 13 dias úteis
            </Titulo>

            <Descricao>
              Prazo válido somente após o presenteado resgatar seu presente.
            </Descricao>
          </CardEntrega>
        </div>

        <div className="row--form row--rua">
          <Input
            erro={ erros.rua }
            label="Rua"
            name="rua"
            onBlur={ handleBlur }
            onChange={ handleChange }
            onPressEnter={ handleSubmit }
            sucesso={ sucesso.rua }
            value={ state.rua }
          />
        </div>

        <div className="row--form row--double row--numero">
          <NumberInput
            column={ 3.5 }
            erro={ erros.numero }
            label="Nº"
            name="numero"
            onBlur={ handleBlur }
            onChange={ handleChange }
            onPressEnter={ handleSubmit }
            sucesso={ sucesso.numero }
            tipo={ Input.TIPO_TEL }
            value={ state.numero }
          />

          <Input
            column={ 1.5 }
            label="Complemento"
            name="complemento"
            onBlur={ handleBlur }
            onChange={ handleChange }
            onPressEnter={ handleSubmit }
            sucesso={ state.complemento ? sucesso.complemento : null }
            value={ state.complemento }
          />
        </div>

        <div className="row--form row--bairro">
          <Input
            erro={ erros.bairro }
            label="Bairro"
            name="bairro"
            onBlur={ handleBlur }
            onChange={ handleChange }
            onPressEnter={ handleSubmit }
            sucesso={ sucesso.bairro }
            value={ state.bairro }
          />
        </div>

        <div className="row--form row--double row--uf">
          <div className="uf--field">
            <Select
              defaultOption={ states.find( ( { 'value': uf } ) => uf === state.uf ) }
              erro={ erros.uf }
              itens={ states }
              label="UF"
              margin="0 0 20px 0"
              name="uf"
              onBlur={ handleBlur }
              onChange={ handleChange }
              onPressEnter={ handleSubmit }
              required={ true }
              sucesso={ sucesso.uf }
            />
          </div>

          <Input
            column={ 1.5 }
            erro={ erros.cidade }
            label="Cidade"
            name="cidade"
            onBlur={ handleBlur }
            onChange={ handleChange }
            onPressEnter={ handleSubmit }
            sucesso={ sucesso.cidade }
            value={ state.cidade }
          />
        </div>

        {onCancelar && onConfirmar ?
          <BotaoWrapper>
            <Button
              className="button__cancelar"
              invertido
              onClick={ onCancelar }
              tema={ Button.BOTAO_CURADORIA }
            >
              Cancelar
            </Button>

            <Button
              data-testid="button__confirmar"
              disabled={ !camposObrigatoriosPreenchidos }
              // onClick={ () => onConfirmar() }
              tema={ Button.BOTAO_CURADORIA }
              type={ Button.TYPE_SUBMIT }
            >
              Confirmar
            </Button>
          </BotaoWrapper> : null}
      </form>
    </WrapperEnderecoForm>
  )
}

EnderecoForm.defaultProps = {
  'endereco': {},
  'enviarEndereco': null,
  'onCancelar': null,
  'onConfirmar': null,
  'tema': StyleGuide.cores.azul
}

EnderecoForm.propTypes = {
  'endereco': object,
  'enviarEndereco': func,
  'onCancelar': func,
  'onConfirmar': func,
  'tema': string
}

export default EnderecoForm
